import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "wrapper",
    "input",
    "providerSelect",
    "maxOptionsInput",
    "minConfidenceInput",
    "searchContextSelect",
    "systemPromptInput",
    "userPromptInput",
    "searchLoading",
    "results",
    "mealItemsList",
    "mealItemsEmpty",
    "mealItemsInputs",
    "mealTotalCalories",
    "mealTotalProtein",
    "mealTotalCarbs",
    "mealTotalFat",
    "originalInputField"
  ]

  static values = {
    url: String,
    aiUrl: String,
    locale: String,
    initialItems: Array,
    searchLoading: String,
    aiLabel: String,
    aiLoading: String,
    aiNotFound: String,
    aiUnavailable: String,
    aiChooseOption: String,
    aiGroundedBadge: String,
    aiSourcesLabel: String,
    aiSourcesTitle: String,
    mealItemRemoveLabel: String,
    mealItemQuantityLabel: String,
    mealItemsEmptyText: String
  }

  connect() {
    this.providerStorageKey = "food_autocomplete_ai_model_option"
    this.paramsStorageKey = "food_autocomplete_ai_runtime_options"
    this.timeout = null
    this.lastFoods = []
    this.lastQuery = ""
    this.feedbackMessage = ""
    this.feedbackTone = "warning"
    this.isSearchInProgress = false
    this.searchRequestId = 0
    this.isAiLookupInProgress = false
    this.aiLookupQuery = null
    this.mealItems = []
    this.nextMealItemIndex = 1

    this.restoreProviderPreference()
    this.restoreAiParams()
    this.hydrateInitialItems()
    this.renderMealItems()

    this.boundClose = this.closeOnClickOutside.bind(this)
    document.addEventListener("click", this.boundClose)
  }

  disconnect() {
    document.removeEventListener("click", this.boundClose)
    if (this.timeout) clearTimeout(this.timeout)
  }

  search() {
    const query = this.inputTarget.value.trim()

    if (this.timeout) clearTimeout(this.timeout)
    this.feedbackMessage = ""

    if (query.length < 2) {
      this.isSearchInProgress = false
      this.setSearchLoading(false)
      this.hideResults()
      return
    }

    this.isSearchInProgress = true
    this.lastQuery = query
    this.setSearchLoading(true)
    this.displayResults(this.lastFoods, query)

    this.timeout = setTimeout(() => {
      this.performSearch(query)
    }, 300)
  }

  async performSearch(query) {
    const requestId = ++this.searchRequestId

    try {
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set("q", query)
      if (this.hasLocaleValue) {
        url.searchParams.set("locale", this.localeValue)
      }
      const response = await fetch(url.toString(), {
        headers: { Accept: "application/json" }
      })
      if (!response.ok) throw new Error(`Search request failed (${response.status})`)
      const data = await response.json()
      if (requestId !== this.searchRequestId) return

      this.isSearchInProgress = false
      this.setSearchLoading(false)
      this.lastFoods = Array.isArray(data) ? data : []
      this.feedbackMessage = ""
      this.displayResults(this.lastFoods, query)
    } catch (error) {
      if (requestId !== this.searchRequestId) return
      console.error("Search failed:", error)
      this.isSearchInProgress = false
      this.setSearchLoading(false)
      this.lastFoods = []
      this.showInlineFeedback(this.aiUnavailableText(), "danger")
      this.displayResults(this.lastFoods, query)
    }
  }

  displayResults(foods, query = this.lastQuery) {
    const safeQuery = query.toString().trim()
    const loadingRow = safeQuery.length >= 2 && this.isSearchInProgress ? this.buildLoadingRow() : ""
    const aiLoadingRow =
      safeQuery.length >= 2 && this.isAiLookupInProgress && this.aiLookupQuery === safeQuery
        ? this.buildAiLookupLoadingRow()
        : ""
    const foodRows = foods.map((food) => this.buildFoodRow(food)).join("")
    const fallbackRow = safeQuery.length >= 2 ? this.buildAiFallbackRow(safeQuery) : ""
    const feedbackRow = this.buildFeedbackRow()

    if (!loadingRow && !aiLoadingRow && !foodRows && !fallbackRow && !feedbackRow) {
      this.hideResults()
      return
    }

    this.resultsTarget.innerHTML = `${loadingRow}${aiLoadingRow}${foodRows}${fallbackRow}${feedbackRow}`
    this.showResults()
  }

  buildLoadingRow() {
    return `
      <div class="autocomplete-item autocomplete-loading px-3 py-2">
        <div class="d-flex align-items-center gap-2">
          <i class="bi bi-arrow-repeat spinning"></i>
          <span>${this.escapeHtml(this.searchLoadingText())}</span>
        </div>
      </div>
    `
  }

  buildAiLookupLoadingRow() {
    return `
      <div class="autocomplete-item autocomplete-loading px-3 py-2">
        <div class="d-flex align-items-center gap-2">
          <i class="bi bi-arrow-repeat spinning"></i>
          <span>${this.escapeHtml(this.aiLoadingText())}</span>
        </div>
      </div>
    `
  }

  buildFoodRow(food) {
    const displayNameRaw = food.display_name || food.name_vi || food.name || ""
    const displayName = this.escapeHtml(displayNameRaw)
    const secondaryNameRaw = this.localeValue === "vi" ? food.name || "" : food.name_vi || ""
    const showSecondary = secondaryNameRaw && secondaryNameRaw !== displayNameRaw
    const calories = Number(food.calories) || 0
    const protein = Number(food.protein) || 0
    const carbs = Number(food.carbs) || 0
    const fat = Number(food.fat) || 0
    const fiber = Number(food.fiber) || 0
    const sources = this.normalizeSources(food.sources)
    const grounded = Boolean(food.grounded) || sources.length > 0
    const servingSize = this.escapeHtml(`${food.serving_size || 100}${food.serving_unit || "g"}`)
    const pendingReview = Boolean(food.pending_review)
    const rowId = this.escapeHtml(String(food.id || ""))
    const foodItemId = pendingReview ? food.food_item_id || "" : food.food_item_id || food.id || ""
    const suggestionId = food.suggestion_id || ""

    return `
      <div class="autocomplete-item px-3 py-2 cursor-pointer"
           data-food-id="${rowId}"
           data-food-item-id="${this.escapeHtml(String(foodItemId))}"
           data-food-suggestion-id="${this.escapeHtml(String(suggestionId))}"
           data-food-name="${this.escapeHtml(displayNameRaw)}"
           data-food-calories="${calories}"
           data-food-carbs="${carbs}"
           data-food-protein="${protein}"
           data-food-fat="${fat}"
           data-food-fiber="${fiber}"
           data-food-grounded="${grounded}"
           data-food-pending-review="${pendingReview}"
           data-food-sources="${this.escapeHtml(JSON.stringify(sources))}"
           data-food-sources-count="${sources.length}"
           data-action="click->food-autocomplete#select">
        <div class="d-flex justify-content-between align-items-start">
          <div>
            <div class="fw-medium">${displayName}</div>
            ${showSecondary ? `<small class="text-muted">${this.escapeHtml(secondaryNameRaw)}</small>` : ""}
          </div>
          <div class="text-end ms-3">
            <span class="badge bg-primary-subtle text-primary rounded-pill mb-1">${Math.round(calories)} cal</span>
            <div class="small text-muted">${servingSize}</div>
          </div>
        </div>
        <div class="mt-2 d-flex gap-2">
          <span class="badge bg-success-subtle text-success rounded-pill">P: ${this.formatMacroValue(protein)}g</span>
          <span class="badge bg-info-subtle text-info rounded-pill">C: ${this.formatMacroValue(carbs)}g</span>
          <span class="badge bg-warning-subtle text-warning rounded-pill">F: ${this.formatMacroValue(fat)}g</span>
        </div>
      </div>
    `
  }

  buildAiFallbackRow(query) {
    if (!this.hasAiUrlValue) return ""

    const isLoading = this.isAiLookupInProgress && this.aiLookupQuery === query
    const fallbackText = this.escapeHtml(isLoading ? this.aiLoadingText() : this.aiLabelText())
    const buttonClass = isLoading ? "autocomplete-ai-fallback is-loading" : "autocomplete-ai-fallback"
    const iconClass = isLoading ? "bi-arrow-repeat spinning" : "bi-stars"

    return `
      <button type="button"
              class="autocomplete-item ${buttonClass} px-3 py-2"
              data-query="${this.escapeHtml(query)}"
              data-action="click->food-autocomplete#lookupWithAi"
              ${isLoading ? "disabled" : ""}>
        <div class="d-flex align-items-center gap-2">
          <i class="bi ${iconClass}"></i>
          <span>${fallbackText}</span>
        </div>
      </button>
    `
  }

  buildFeedbackRow() {
    if (!this.feedbackMessage) return ""

    const allowedTones = ["danger", "warning", "info"]
    const toneClass = allowedTones.includes(this.feedbackTone) ? this.feedbackTone : "warning"
    return `<div class="autocomplete-feedback autocomplete-feedback-${toneClass} px-3 py-2">${this.escapeHtml(this.feedbackMessage)}</div>`
  }

  showInlineFeedback(message, tone = "warning") {
    this.feedbackMessage = message
    this.feedbackTone = tone
  }

  select(event) {
    const item = event.currentTarget
    const sources = this.parseSources(item.dataset.foodSources)
    const foodData = {
      id: item.dataset.foodId,
      foodItemId: item.dataset.foodItemId,
      suggestionId: item.dataset.foodSuggestionId,
      name: item.dataset.foodName,
      calories: parseFloat(item.dataset.foodCalories),
      carbs: parseFloat(item.dataset.foodCarbs),
      protein: parseFloat(item.dataset.foodProtein),
      fat: parseFloat(item.dataset.foodFat),
      fiber: parseFloat(item.dataset.foodFiber),
      grounded: item.dataset.foodGrounded === "true",
      pendingReview: item.dataset.foodPendingReview === "true",
      sources: sources
    }

    this.addFoodToMeal(foodData)
  }

  addFoodToMeal(foodData) {
    const mealItem = {
      clientId: this.generateClientId(),
      displayName: foodData.name,
      originalInput: foodData.name,
      foodItemId: foodData.foodItemId || "",
      aiSuggestionId: foodData.suggestionId || "",
      quantity: 100,
      unit: "g",
      baseCalories: Number(foodData.calories) || 0,
      baseProtein: Number(foodData.protein) || 0,
      baseCarbs: Number(foodData.carbs) || 0,
      baseFat: Number(foodData.fat) || 0,
      baseFiber: Number(foodData.fiber) || 0,
      grounded: Boolean(foodData.grounded),
      pendingReview: Boolean(foodData.pendingReview),
      sources: this.normalizeSources(foodData.sources),
      errors: []
    }

    this.mealItems.push(mealItem)
    this.renderMealItems()

    this.inputTarget.value = ""
    this.setSearchLoading(false)
    this.hideResults()
    this.flashMealList()
  }

  updateMealItemQuantity(event) {
    const itemId = event.currentTarget.dataset.itemId
    const item = this.mealItems.find((entry) => entry.clientId === itemId)
    if (!item) return

    const quantity = parseFloat(event.currentTarget.value)
    if (Number.isFinite(quantity) && quantity > 0) {
      item.quantity = quantity
      event.currentTarget.classList.remove("is-invalid")
    } else {
      item.quantity = 0
      event.currentTarget.classList.add("is-invalid")
    }

    item.errors = []
    this.refreshMealItemCard(item)
    this.renderHiddenInputs()
    this.updateMealTotals()
    this.updateOriginalInputField()
  }

  removeMealItem(event) {
    event.preventDefault()
    const itemId = event.currentTarget.dataset.itemId
    this.mealItems = this.mealItems.filter((item) => item.clientId !== itemId)
    this.renderMealItems()
  }

  async lookupWithAi(event) {
    event.preventDefault()
    if (!this.hasAiUrlValue || this.isAiLookupInProgress) return

    const query = (event.currentTarget.dataset.query || this.inputTarget.value || "").trim()
    if (query.length < 2) return

    this.isAiLookupInProgress = true
    this.aiLookupQuery = query
    this.feedbackMessage = ""
    this.setSearchLoading(true)
    this.displayResults(this.lastFoods, query)

    try {
      const payload = await this.postAiLookup(query)
      const aiFoods = this.extractAiFoods(payload)
      if (aiFoods.length === 0) {
        const error = new Error(this.aiNotFoundText())
        error.type = "not_found"
        throw error
      }

      this.lastFoods = aiFoods
      this.lastQuery = query
      this.showInlineFeedback(this.aiChooseOptionText(aiFoods.length), "info")
      this.displayResults(this.lastFoods, query)
    } catch (error) {
      if (error.type === "not_found" || error.type === "invalid") {
        this.showInlineFeedback(error.message || this.aiNotFoundText(), "warning")
      } else {
        this.showInlineFeedback(error.message || this.aiUnavailableText(), "danger")
      }
      this.displayResults(this.lastFoods, query)
    } finally {
      this.isAiLookupInProgress = false
      this.aiLookupQuery = null
      this.setSearchLoading(false)
      const currentQuery = this.inputTarget.value.trim()
      if (currentQuery.length >= 2) {
        this.displayResults(this.lastFoods, currentQuery)
      }
    }
  }

  async postAiLookup(query) {
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
    const response = await fetch(this.aiUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": csrfToken
      },
      body: JSON.stringify({
        q: query,
        locale: this.localeValue,
        provider: this.selectedProvider(),
        model: this.selectedModel(),
        model_option: this.selectedModelOption(),
        runtime_options: this.collectAiRuntimeOptions()
      })
    })

    let payload = {}
    try {
      payload = await response.json()
    } catch {
      payload = {}
    }

    if (response.ok && payload.food) {
      return payload
    }

    const error = new Error(payload.error || this.aiUnavailableText())
    if (response.status === 404) {
      error.type = "not_found"
    } else if (response.status === 422) {
      error.type = "invalid"
    } else {
      error.type = "unavailable"
    }
    throw error
  }

  extractAiFoods(payload) {
    const foods = Array.isArray(payload.foods) ? payload.foods : payload.food ? [payload.food] : []
    return foods.filter((food) => food && food.id).map((food) => {
      const normalizedSources = this.normalizeSources(food.sources)
      return {
        ...food,
        food_item_id: food.food_item_id || "",
        suggestion_id: food.suggestion_id || food.id,
        grounded: Boolean(food.grounded) || normalizedSources.length > 0,
        pending_review: food.pending_review !== false,
        sources: normalizedSources
      }
    })
  }

  renderMealItems() {
    this.renderMealItemList()
    this.renderHiddenInputs()
    this.updateMealTotals()
    this.updateOriginalInputField()
    this.toggleMealItemsEmptyState()
  }

  renderMealItemList() {
    if (!this.hasMealItemsListTarget) return

    if (this.mealItems.length === 0) {
      this.mealItemsListTarget.innerHTML = ""
      return
    }

    this.mealItemsListTarget.innerHTML = this.mealItems
      .map((item, index) => this.buildMealItemCard(item, index))
      .join("")
  }

  buildMealItemCard(item, index) {
    const metrics = this.metricsForItem(item)
    const groundedBadge = item.grounded
      ? `<span class="badge bg-secondary-subtle text-secondary rounded-pill">${this.escapeHtml(this.aiGroundedBadgeText())}</span>`
      : ""
    const errorBlock = this.buildMealItemErrorBlock(item)

    return `
      <div class="meal-item-row border rounded-3 p-3 mb-2" data-meal-item-id="${this.escapeHtml(item.clientId)}">
        <div class="d-flex justify-content-between align-items-start gap-2">
          <div class="flex-grow-1">
            <div class="fw-semibold">${this.escapeHtml(item.displayName || item.originalInput)}</div>
            <div class="small text-muted">#${index + 1}</div>
          </div>
          <div class="d-flex align-items-center gap-2">
            ${groundedBadge}
            <button type="button"
                    class="btn btn-sm btn-outline-danger"
                    data-item-id="${this.escapeHtml(item.clientId)}"
                    data-action="click->food-autocomplete#removeMealItem">
              ${this.escapeHtml(this.mealItemRemoveLabelText())}
            </button>
          </div>
        </div>

        <div class="row g-2 mt-1 align-items-end">
          <div class="col-5">
            <label class="form-label mb-1 small">${this.escapeHtml(this.mealItemQuantityLabelText())}</label>
            <div class="input-group input-group-sm">
              <input type="number"
                     min="0.01"
                     step="0.01"
                     class="form-control"
                     value="${this.escapeHtml(item.quantity > 0 ? item.quantity : "")}"
                     data-item-id="${this.escapeHtml(item.clientId)}"
                     data-action="input->food-autocomplete#updateMealItemQuantity">
              <span class="input-group-text">g</span>
            </div>
          </div>

          <div class="col-7">
            <div class="meal-item-metrics d-flex flex-wrap gap-1 justify-content-end">
              <span class="badge bg-primary-subtle text-primary rounded-pill"><span data-role="calories">${metrics.calories}</span> cal</span>
              <span class="badge bg-success-subtle text-success rounded-pill">P: <span data-role="protein">${this.formatMacroValue(metrics.protein)}</span>g</span>
              <span class="badge bg-info-subtle text-info rounded-pill">C: <span data-role="carbs">${this.formatMacroValue(metrics.carbs)}</span>g</span>
              <span class="badge bg-warning-subtle text-warning rounded-pill">F: <span data-role="fat">${this.formatMacroValue(metrics.fat)}</span>g</span>
            </div>
          </div>
        </div>

        ${errorBlock}
      </div>
    `
  }

  buildMealItemErrorBlock(item) {
    if (!Array.isArray(item.errors) || item.errors.length === 0) return ""

    const errors = item.errors
      .map((message) => `<li>${this.escapeHtml(message)}</li>`)
      .join("")

    return `
      <div class="meal-item-errors alert alert-danger py-2 px-3 mt-2 mb-0">
        <ul class="mb-0 ps-3">
          ${errors}
        </ul>
      </div>
    `
  }

  refreshMealItemCard(item) {
    if (!this.hasMealItemsListTarget) return

    const row = this.mealItemsListTarget.querySelector(`[data-meal-item-id="${this.escapeHtml(item.clientId)}"]`)
    if (!row) {
      this.renderMealItemList()
      return
    }

    const metrics = this.metricsForItem(item)
    const caloriesElement = row.querySelector("[data-role='calories']")
    const proteinElement = row.querySelector("[data-role='protein']")
    const carbsElement = row.querySelector("[data-role='carbs']")
    const fatElement = row.querySelector("[data-role='fat']")

    if (caloriesElement) caloriesElement.textContent = String(metrics.calories)
    if (proteinElement) proteinElement.textContent = this.formatMacroValue(metrics.protein)
    if (carbsElement) carbsElement.textContent = this.formatMacroValue(metrics.carbs)
    if (fatElement) fatElement.textContent = this.formatMacroValue(metrics.fat)

    const existingErrors = row.querySelector(".meal-item-errors")
    if (existingErrors) existingErrors.remove()
  }

  renderHiddenInputs() {
    if (!this.hasMealItemsInputsTarget) return

    const inputs = []

    this.mealItems.forEach((item, index) => {
      const metrics = this.metricsForItem(item)
      const sourceJson = JSON.stringify(this.normalizeSources(item.sources))

      inputs.push(this.buildHiddenInput(`food_entry[meal_items][${index}][client_id]`, item.clientId))
      inputs.push(this.buildHiddenInput(`food_entry[meal_items][${index}][display_name]`, item.displayName))
      inputs.push(this.buildHiddenInput(`food_entry[meal_items][${index}][original_input]`, item.originalInput))
      inputs.push(this.buildHiddenInput(`food_entry[meal_items][${index}][food_item_id]`, item.foodItemId))
      inputs.push(this.buildHiddenInput(`food_entry[meal_items][${index}][ai_suggestion_id]`, item.aiSuggestionId))
      inputs.push(this.buildHiddenInput(`food_entry[meal_items][${index}][quantity]`, item.quantity))
      inputs.push(this.buildHiddenInput(`food_entry[meal_items][${index}][unit]`, item.unit || "g"))
      inputs.push(this.buildHiddenInput(`food_entry[meal_items][${index}][estimated_calories]`, metrics.calories))
      inputs.push(this.buildHiddenInput(`food_entry[meal_items][${index}][protein]`, metrics.protein))
      inputs.push(this.buildHiddenInput(`food_entry[meal_items][${index}][carbs]`, metrics.carbs))
      inputs.push(this.buildHiddenInput(`food_entry[meal_items][${index}][fat]`, metrics.fat))
      inputs.push(this.buildHiddenInput(`food_entry[meal_items][${index}][fiber]`, metrics.fiber))
      inputs.push(this.buildHiddenInput(`food_entry[meal_items][${index}][grounded]`, item.grounded))
      inputs.push(this.buildHiddenInput(`food_entry[meal_items][${index}][pending_review]`, item.pendingReview))
      inputs.push(this.buildHiddenInput(`food_entry[meal_items][${index}][sources]`, sourceJson))
    })

    this.mealItemsInputsTarget.innerHTML = inputs.join("")
  }

  buildHiddenInput(name, value) {
    const normalized = value === null || value === undefined ? "" : String(value)
    return `<input type="hidden" name="${this.escapeHtml(name)}" value="${this.escapeHtml(normalized)}">`
  }

  updateMealTotals() {
    const totals = this.mealItems.reduce(
      (acc, item) => {
        const metrics = this.metricsForItem(item)
        acc.calories += metrics.calories
        acc.protein += metrics.protein
        acc.carbs += metrics.carbs
        acc.fat += metrics.fat
        return acc
      },
      { calories: 0, protein: 0, carbs: 0, fat: 0 }
    )

    if (this.hasMealTotalCaloriesTarget) {
      this.mealTotalCaloriesTarget.textContent = String(Math.round(totals.calories))
    }
    if (this.hasMealTotalProteinTarget) {
      this.mealTotalProteinTarget.textContent = this.formatMacroValue(totals.protein)
    }
    if (this.hasMealTotalCarbsTarget) {
      this.mealTotalCarbsTarget.textContent = this.formatMacroValue(totals.carbs)
    }
    if (this.hasMealTotalFatTarget) {
      this.mealTotalFatTarget.textContent = this.formatMacroValue(totals.fat)
    }
  }

  updateOriginalInputField() {
    if (!this.hasOriginalInputFieldTarget) return
    const names = this.mealItems
      .map((item) => item.originalInput)
      .filter((value) => value && value.length > 0)
    this.originalInputFieldTarget.value = names.join(", ")
  }

  toggleMealItemsEmptyState() {
    if (!this.hasMealItemsEmptyTarget) return

    if (this.mealItems.length === 0) {
      this.mealItemsEmptyTarget.classList.remove("d-none")
    } else {
      this.mealItemsEmptyTarget.classList.add("d-none")
    }
  }

  metricsForItem(item) {
    const quantity = this.normalizePositiveFloat(item.quantity, 0)
    const factor = quantity > 0 ? quantity / 100 : 0

    return {
      calories: Math.round((item.baseCalories || 0) * factor),
      protein: (item.baseProtein || 0) * factor,
      carbs: (item.baseCarbs || 0) * factor,
      fat: (item.baseFat || 0) * factor,
      fiber: (item.baseFiber || 0) * factor
    }
  }

  hydrateInitialItems() {
    const source = this.hasInitialItemsValue && Array.isArray(this.initialItemsValue) ? this.initialItemsValue : []
    if (source.length === 0) return

    this.mealItems = source.map((item, index) => this.normalizeMealItem(item, index))
    this.nextMealItemIndex = this.mealItems.length + 1
  }

  normalizeMealItem(raw, index) {
    const quantity = this.normalizePositiveFloat(raw.quantity, 100)
    const estimatedCalories = this.normalizePositiveFloat(raw.estimated_calories, 0)
    const protein = this.normalizePositiveFloat(raw.protein, 0)
    const carbs = this.normalizePositiveFloat(raw.carbs, 0)
    const fat = this.normalizePositiveFloat(raw.fat, 0)
    const fiber = this.normalizePositiveFloat(raw.fiber, 0)

    const baseCalories = this.normalizePositiveFloat(raw.base_calories, quantity > 0 ? (estimatedCalories * 100) / quantity : 0)
    const baseProtein = this.normalizePositiveFloat(raw.base_protein, quantity > 0 ? (protein * 100) / quantity : 0)
    const baseCarbs = this.normalizePositiveFloat(raw.base_carbs, quantity > 0 ? (carbs * 100) / quantity : 0)
    const baseFat = this.normalizePositiveFloat(raw.base_fat, quantity > 0 ? (fat * 100) / quantity : 0)
    const baseFiber = this.normalizePositiveFloat(raw.base_fiber, quantity > 0 ? (fiber * 100) / quantity : 0)

    return {
      clientId: this.sanitizeClientId(raw.client_id || `meal_item_${index + 1}`),
      displayName: (raw.display_name || raw.original_input || "").toString().trim(),
      originalInput: (raw.original_input || raw.display_name || "").toString().trim(),
      foodItemId: (raw.food_item_id || "").toString().trim(),
      aiSuggestionId: (raw.ai_suggestion_id || "").toString().trim(),
      quantity: quantity,
      unit: (raw.unit || "g").toString().trim() || "g",
      baseCalories: baseCalories,
      baseProtein: baseProtein,
      baseCarbs: baseCarbs,
      baseFat: baseFat,
      baseFiber: baseFiber,
      grounded: Boolean(raw.grounded),
      pendingReview: Boolean(raw.pending_review),
      sources: this.normalizeSources(raw.sources),
      errors: Array.isArray(raw.errors) ? raw.errors : []
    }
  }

  generateClientId() {
    const id = `meal_item_${this.nextMealItemIndex}`
    this.nextMealItemIndex += 1
    return id
  }

  sanitizeClientId(value) {
    const normalized = value.toString().replace(/[^a-zA-Z0-9_-]/g, "")
    return normalized || this.generateClientId()
  }

  normalizePositiveFloat(value, fallback = 0) {
    const parsed = Number.parseFloat(value)
    if (!Number.isFinite(parsed) || parsed < 0) return fallback
    return parsed
  }

  showResults() {
    this.resultsTarget.classList.remove("d-none")
    const wrapper = this.hasWrapperTarget ? this.wrapperTarget : this.element
    wrapper.classList.add("autocomplete-open")
  }

  hideResults() {
    this.resultsTarget.classList.add("d-none")
    const wrapper = this.hasWrapperTarget ? this.wrapperTarget : this.element
    wrapper.classList.remove("autocomplete-open")
  }

  setSearchLoading(isLoading) {
    if (!this.hasSearchLoadingTarget) return

    if (isLoading) {
      this.searchLoadingTarget.classList.remove("d-none")
    } else {
      this.searchLoadingTarget.classList.add("d-none")
    }
  }

  closeOnClickOutside(event) {
    const wrapper = this.hasWrapperTarget ? this.wrapperTarget : this.element
    if (!wrapper.contains(event.target)) {
      this.hideResults()
    }
  }

  flashMealList() {
    if (!this.hasMealItemsListTarget) return

    this.mealItemsListTarget.classList.add("flash-success")
    setTimeout(() => {
      this.mealItemsListTarget.classList.remove("flash-success")
    }, 600)
  }

  hasValue(value) {
    return Number.isFinite(value) && value > 0
  }

  formatMacroValue(value) {
    const rounded = Math.round(Number(value || 0) * 10) / 10
    return Number.isInteger(rounded) ? rounded.toString() : rounded.toFixed(1)
  }

  mealItemRemoveLabelText() {
    if (this.hasMealItemRemoveLabelValue) return this.mealItemRemoveLabelValue
    return this.localeValue === "vi" ? "Xóa" : "Remove"
  }

  mealItemQuantityLabelText() {
    if (this.hasMealItemQuantityLabelValue) return this.mealItemQuantityLabelValue
    return this.localeValue === "vi" ? "Khối lượng" : "Grams"
  }

  aiLabelText() {
    if (this.hasAiLabelValue) return this.aiLabelValue
    return this.localeValue === "vi" ? "Tìm kiếm bằng AI" : "Not found? Use AI to search"
  }

  searchLoadingText() {
    if (this.hasSearchLoadingValue) return this.searchLoadingValue
    return this.localeValue === "vi" ? "Đang tìm món ăn..." : "Searching foods..."
  }

  aiLoadingText() {
    if (this.hasAiLoadingValue) return this.aiLoadingValue
    return this.localeValue === "vi" ? "Đang tìm bằng AI..." : "Searching with AI..."
  }

  aiNotFoundText() {
    if (this.hasAiNotFoundValue) return this.aiNotFoundValue
    return this.localeValue === "vi" ? "Không tìm được kết quả có nguồn tin cậy." : "No grounded result found."
  }

  aiUnavailableText() {
    if (this.hasAiUnavailableValue) return this.aiUnavailableValue
    return this.localeValue === "vi" ? "Dịch vụ AI tạm thời không khả dụng." : "AI service is temporarily unavailable."
  }

  aiChooseOptionText(count) {
    if (this.hasAiChooseOptionValue) {
      return this.aiChooseOptionValue.replace("%{count}", String(count))
    }
    if (this.localeValue === "vi") {
      return `Đã tìm thấy ${count} lựa chọn AI có nguồn. Hãy chọn 1 món.`
    }
    return `Found ${count} grounded AI options. Please choose one.`
  }

  aiGroundedBadgeText() {
    if (this.hasAiGroundedBadgeValue) return this.aiGroundedBadgeValue
    return this.localeValue === "vi" ? "AI có nguồn" : "AI grounded"
  }

  aiSourcesLabelText() {
    if (this.hasAiSourcesLabelValue) return this.aiSourcesLabelValue
    return this.localeValue === "vi" ? "nguồn" : "sources"
  }

  aiSourcesTitleText() {
    if (this.hasAiSourcesTitleValue) return this.aiSourcesTitleValue
    return this.localeValue === "vi" ? "Nguồn tham khảo (bấm để kiểm chứng)" : "Sources (tap to verify)"
  }

  parseSources(rawSources) {
    if (!rawSources) return []
    try {
      const parsed = JSON.parse(rawSources)
      return this.normalizeSources(parsed)
    } catch {
      return []
    }
  }

  normalizeSources(sources) {
    let normalizedArray = sources
    if (typeof sources === "string") {
      try {
        normalizedArray = JSON.parse(sources)
      } catch {
        normalizedArray = []
      }
    }

    if (!Array.isArray(normalizedArray)) return []

    return normalizedArray
      .map((source) => {
        if (!source || typeof source !== "object") return null

        const url = this.sanitizeHttpUrl(source.url)
        if (!url) return null

        const title = source.title?.toString().trim() || url
        return { title, url }
      })
      .filter(Boolean)
  }

  sanitizeHttpUrl(rawUrl) {
    if (!rawUrl) return null
    try {
      const parsed = new URL(rawUrl)
      if (!["http:", "https:"].includes(parsed.protocol)) return null
      return parsed.toString()
    } catch {
      return null
    }
  }

  conciseSourceLabel(source, index, totalCount) {
    const host = this.hostnameFromUrl(source.url)
    if (host && totalCount > 1) return `${host} (${index + 1})`
    if (host) return host

    const title = source.title?.toString().trim() || source.url
    return title.length > 42 ? `${title.slice(0, 39)}...` : title
  }

  hostnameFromUrl(rawUrl) {
    try {
      return new URL(rawUrl).hostname.replace(/^www\./, "")
    } catch {
      return ""
    }
  }

  updateProvider() {
    if (!this.hasProviderSelectTarget) return
    window.localStorage?.setItem(this.providerStorageKey, this.selectedModelOption())
  }

  updateAiParams() {
    const payload = this.collectAiRuntimeOptions()
    window.localStorage?.setItem(this.paramsStorageKey, JSON.stringify(payload))
  }

  resetAiParams(event) {
    if (event) event.preventDefault()

    if (this.hasMaxOptionsInputTarget) this.maxOptionsInputTarget.value = "3"
    if (this.hasMinConfidenceInputTarget) this.minConfidenceInputTarget.value = "0"
    if (this.hasSearchContextSelectTarget) this.searchContextSelectTarget.value = "medium"
    if (this.hasSystemPromptInputTarget) this.systemPromptInputTarget.value = ""
    if (this.hasUserPromptInputTarget) this.userPromptInputTarget.value = ""

    window.localStorage?.removeItem(this.paramsStorageKey)
  }

  restoreProviderPreference() {
    if (!this.hasProviderSelectTarget) return

    const savedProvider = window.localStorage?.getItem(this.providerStorageKey)
    if (this.modelOptions().includes(savedProvider)) {
      this.providerSelectTarget.value = savedProvider
    }
  }

  restoreAiParams() {
    const defaults = this.defaultAiRuntimeOptions()
    if (this.hasMaxOptionsInputTarget) this.maxOptionsInputTarget.value = String(defaults.max_options)
    if (this.hasMinConfidenceInputTarget) this.minConfidenceInputTarget.value = String(defaults.min_confidence)
    if (this.hasSearchContextSelectTarget) this.searchContextSelectTarget.value = defaults.search_context_size
    if (this.hasSystemPromptInputTarget) this.systemPromptInputTarget.value = defaults.system_prompt_override
    if (this.hasUserPromptInputTarget) this.userPromptInputTarget.value = defaults.user_prompt_override

    const saved = window.localStorage?.getItem(this.paramsStorageKey)
    if (!saved) return

    try {
      const parsed = JSON.parse(saved)
      const normalized = this.normalizeAiRuntimeOptions(parsed)
      if (this.hasMaxOptionsInputTarget) this.maxOptionsInputTarget.value = String(normalized.max_options)
      if (this.hasMinConfidenceInputTarget) this.minConfidenceInputTarget.value = String(normalized.min_confidence)
      if (this.hasSearchContextSelectTarget) this.searchContextSelectTarget.value = normalized.search_context_size
      if (this.hasSystemPromptInputTarget) this.systemPromptInputTarget.value = normalized.system_prompt_override
      if (this.hasUserPromptInputTarget) this.userPromptInputTarget.value = normalized.user_prompt_override
    } catch {
      window.localStorage?.removeItem(this.paramsStorageKey)
    }
  }

  collectAiRuntimeOptions() {
    const options = this.normalizeAiRuntimeOptions({
      max_options: this.hasMaxOptionsInputTarget ? this.maxOptionsInputTarget.value : undefined,
      min_confidence: this.hasMinConfidenceInputTarget ? this.minConfidenceInputTarget.value : undefined,
      search_context_size: this.hasSearchContextSelectTarget ? this.searchContextSelectTarget.value : undefined,
      system_prompt_override: this.hasSystemPromptInputTarget ? this.systemPromptInputTarget.value : undefined,
      user_prompt_override: this.hasUserPromptInputTarget ? this.userPromptInputTarget.value : undefined
    })

    return {
      max_options: options.max_options,
      min_confidence: options.min_confidence,
      search_context_size: options.search_context_size,
      system_prompt_override: options.system_prompt_override,
      user_prompt_override: options.user_prompt_override
    }
  }

  normalizeAiRuntimeOptions(rawOptions) {
    const defaults = this.defaultAiRuntimeOptions()
    const maxOptions = this.clampInteger(rawOptions?.max_options, 1, 5, defaults.max_options)
    const minConfidence = this.clampFloat(rawOptions?.min_confidence, 0, 1, defaults.min_confidence)
    const searchContext = this.normalizeSearchContext(rawOptions?.search_context_size, defaults.search_context_size)
    const systemPrompt = this.normalizePrompt(rawOptions?.system_prompt_override)
    const userPrompt = this.normalizePrompt(rawOptions?.user_prompt_override)

    return {
      max_options: maxOptions,
      min_confidence: minConfidence,
      search_context_size: searchContext,
      system_prompt_override: systemPrompt,
      user_prompt_override: userPrompt
    }
  }

  defaultAiRuntimeOptions() {
    return {
      max_options: 5,
      min_confidence: 0,
      search_context_size: "medium",
      system_prompt_override: "",
      user_prompt_override: ""
    }
  }

  normalizePrompt(rawValue) {
    const text = (rawValue || "").toString().trim()
    if (!text) return ""
    return text.slice(0, 1200)
  }

  normalizeSearchContext(value, fallback) {
    const normalized = value?.toString().trim().toLowerCase()
    return ["low", "medium", "high"].includes(normalized) ? normalized : fallback
  }

  clampInteger(value, min, max, fallback) {
    const parsed = Number.parseInt(value, 10)
    if (!Number.isFinite(parsed)) return fallback
    return Math.min(max, Math.max(min, parsed))
  }

  clampFloat(value, min, max, fallback) {
    const parsed = Number.parseFloat(value)
    if (!Number.isFinite(parsed)) return fallback
    const clamped = Math.min(max, Math.max(min, parsed))
    return Math.round(clamped * 100) / 100
  }

  selectedProvider() {
    const option = this.selectedModelOption()
    return option.startsWith("gemini") ? "gemini" : "openai"
  }

  selectedModel() {
    const option = this.selectedModelOption()
    switch (option) {
      case "openai_gpt5_2":
        return "gpt-5.2"
      case "gemini_pro":
        return "gemini-3.1-pro-preview"
      case "gemini_flash":
        return "gemini-3-flash-preview"
      case "openai_gpt5_1":
      default:
        return "gpt-5.1"
    }
  }

  selectedModelOption() {
    if (!this.hasProviderSelectTarget) return "openai_gpt5_1"

    const value = this.providerSelectTarget.value?.toString().trim().toLowerCase()
    return this.modelOptions().includes(value) ? value : "openai_gpt5_1"
  }

  modelOptions() {
    return ["openai_gpt5_1", "openai_gpt5_2", "gemini_pro", "gemini_flash"]
  }

  escapeHtml(value) {
    return value
      .toString()
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;")
  }
}
